/**
 * Strokes and the pen timeline.
 *
 * A reveal plan's strokes are resampled to evenly spaced points. Revealing is counted in whole
 * points: "R points revealed" means points [0, R) have been stamped into the mask. Because each
 * point is always stamped by the same canvas call, building a mask incrementally (frame after
 * frame) or from scratch (after a seek) issues the same sequence of draw calls and gives identical
 * pixels.
 *
 * The pen timeline turns the strokes into draw segments and pen-up travel segments and fits them
 * into a phase duration: travel is quick but always visible, draw time follows stroke length.
 */

import { clamp, lerp, smoothstep } from "./math";

export interface PlanStroke {
  /** flat [x0, y0, x1, y1, ...] in layer pixels */
  p: number[];
  /** brush radius per point, or one radius for the whole stroke */
  r: number | number[];
}

export class StrokeSet {
  constructor(
    readonly x: Float64Array,
    readonly y: Float64Array,
    readonly r: Float64Array,
    /** index of each stroke's first point */
    readonly first: Int32Array,
    /** number of points per stroke */
    readonly count: Int32Array,
    /** arc length per stroke */
    readonly len: Float64Array,
  ) {}

  get strokes(): number {
    return this.first.length;
  }
  get total(): number {
    return this.x.length;
  }
  /** index of the stroke that owns point i */
  strokeOf(i: number): number {
    let lo = 0;
    let hi = this.first.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (this.first[mid] <= i) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }
}

export type PointXform = (x: number, y: number, r: number) => [number, number, number];

/** Resample strokes to uniform arc-length spacing (step ≈ 0.6 × brush radius, 1.5–6 px). */
export function buildStrokeSet(strokes: PlanStroke[], xform?: PointXform): StrokeSet {
  const xs: number[] = [];
  const ys: number[] = [];
  const rs: number[] = [];
  const first: number[] = [];
  const count: number[] = [];
  const lens: number[] = [];
  for (const s of strokes) {
    const n = s.p.length >> 1;
    if (n === 0) continue;
    let px = new Array<number>(n);
    let py = new Array<number>(n);
    let pr = new Array<number>(n);
    for (let i = 0; i < n; i++) {
      let x = s.p[2 * i];
      let y = s.p[2 * i + 1];
      let r = typeof s.r === "number" ? s.r : s.r[i];
      if (xform) [x, y, r] = xform(x, y, r);
      px[i] = x;
      py[i] = y;
      pr[i] = r;
    }
    // cumulative length
    const cum = new Array<number>(n);
    cum[0] = 0;
    for (let i = 1; i < n; i++) cum[i] = cum[i - 1] + Math.hypot(px[i] - px[i - 1], py[i] - py[i - 1]);
    const L = cum[n - 1];
    first.push(xs.length);
    lens.push(L);
    if (n === 1 || L === 0) {
      xs.push(px[0]);
      ys.push(py[0]);
      rs.push(Math.max(...pr));
      count.push(1);
      continue;
    }
    let meanR = 0;
    for (const r of pr) meanR += r;
    meanR /= n;
    const step = clamp(meanR * 0.6, 1.5, 6);
    const m = Math.max(2, Math.ceil(L / step) + 1);
    let j = 0;
    for (let k = 0; k < m; k++) {
      const s_ = (L * k) / (m - 1);
      while (j < n - 2 && cum[j + 1] < s_) j++;
      const seg = cum[j + 1] - cum[j];
      const u = seg > 0 ? clamp((s_ - cum[j]) / seg, 0, 1) : 0;
      xs.push(lerp(px[j], px[j + 1], u));
      ys.push(lerp(py[j], py[j + 1], u));
      rs.push(lerp(pr[j], pr[j + 1], u));
    }
    count.push(m);
    px = py = pr = [];
  }
  return new StrokeSet(
    Float64Array.from(xs),
    Float64Array.from(ys),
    Float64Array.from(rs),
    Int32Array.from(first),
    Int32Array.from(count),
    Float64Array.from(lens),
  );
}

// ------------------------------------------------------------------------------------------ pen

export interface PenOptions {
  /** phase length in seconds */
  duration: number;
  /** board units per layer pixel (travel speed is defined in board units) */
  unitScale: number;
  /** pen position before the first stroke (layer px); adds a travel at the start of the phase */
  startAt?: [number, number] | null;
  travelSpeed?: number; // board units / s
  travelMin?: number; // s
  travelMax?: number; // s
  maxTravelShare?: number; // max fraction of the phase spent travelling
  strokeOverhead?: number; // layer px added to every stroke's length when sharing out draw time
}

export interface PenState {
  x: number;
  y: number;
  /** pen touching the board (drawing), false while travelling */
  down: boolean;
  /** number of points revealed */
  revealed: number;
}

interface Seg {
  t0: number;
  t1: number;
  /** stroke index for draw segments, -1 for travel */
  stroke: number;
  ax: number;
  ay: number;
  bx: number;
  by: number;
}

export class PenTimeline {
  readonly segs: Seg[] = [];
  readonly travelTime: number;

  constructor(
    readonly set: StrokeSet,
    readonly opts: PenOptions,
  ) {
    const speed = opts.travelSpeed ?? 2600;
    const tMin = opts.travelMin ?? 0.034; // about one frame at 30 fps
    const tMax = opts.travelMax ?? 0.25;
    const share = opts.maxTravelShare ?? 0.4;
    const overhead = opts.strokeOverhead ?? 6;
    const D = Math.max(0, opts.duration);
    const n = set.strokes;

    // travel before each stroke: desired time from distance (board units)
    const desired: number[] = [];
    const dist: number[] = [];
    let prev: [number, number] | null = opts.startAt ?? null;
    for (let k = 0; k < n; k++) {
      const i = set.first[k];
      const d = prev ? Math.hypot(set.x[i] - prev[0], set.y[i] - prev[1]) * opts.unitScale : 0;
      dist.push(d);
      desired.push(prev && d >= 0.5 ? clamp(0.02 + d / speed, tMin, tMax) : 0);
      const e = i + set.count[k] - 1;
      prev = [set.x[e], set.y[e]];
    }
    // the opening travel (from where the hand was) is a transition, not a hop: always keep it whole
    const travel = fitTravel(desired, dist, share * D, { exemptFirst: !!opts.startAt });
    const tt = travel.reduce((a, b) => a + b, 0);
    this.travelTime = tt;
    const drawTotal = Math.max(0, D - tt);
    let wsum = 0;
    for (let k = 0; k < n; k++) wsum += set.len[k] + overhead;

    let t = 0;
    prev = opts.startAt ?? null;
    for (let k = 0; k < n; k++) {
      const i = set.first[k];
      const e = i + set.count[k] - 1;
      if (travel[k] > 0 && prev) {
        this.segs.push({ t0: t, t1: t + travel[k], stroke: -1, ax: prev[0], ay: prev[1], bx: set.x[i], by: set.y[i] });
        t += travel[k];
      }
      const dt = wsum > 0 ? (drawTotal * (set.len[k] + overhead)) / wsum : 0;
      this.segs.push({ t0: t, t1: t + dt, stroke: k, ax: set.x[i], ay: set.y[i], bx: set.x[e], by: set.y[e] });
      t += dt;
      prev = [set.x[e], set.y[e]];
    }
  }

  get duration(): number {
    return this.opts.duration;
  }

  /** First pen position (start of the first segment), or null when there is nothing to draw. */
  get startPoint(): [number, number] | null {
    const s = this.segs[0];
    return s ? [s.ax, s.ay] : null;
  }

  get endPoint(): [number, number] | null {
    const s = this.segs[this.segs.length - 1];
    return s ? [s.bx, s.by] : null;
  }

  /** Pen state at time t (seconds from the start of the phase). */
  at(t: number): PenState {
    const set = this.set;
    const segs = this.segs;
    if (segs.length === 0) return { x: 0, y: 0, down: false, revealed: set.total };
    if (t < segs[0].t0) {
      return { x: segs[0].ax, y: segs[0].ay, down: false, revealed: 0 };
    }
    const last = segs[segs.length - 1];
    if (t >= last.t1) return { x: last.bx, y: last.by, down: false, revealed: set.total };
    // binary search for the segment containing t
    let lo = 0;
    let hi = segs.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (segs[mid].t0 <= t) lo = mid;
      else hi = mid - 1;
    }
    const s = segs[lo];
    const span = s.t1 - s.t0;
    const u = span > 0 ? clamp((t - s.t0) / span, 0, 1) : 1;
    if (s.stroke < 0) {
      // pen-up travel: smoothstep (peak speed 1.5× average), with a slight arc as the hand lifts
      const e = smoothstep(u);
      const dx = s.bx - s.ax;
      const dy = s.by - s.ay;
      const lift = Math.sin(Math.PI * e) * 0.08;
      return {
        x: s.ax + dx * e + dy * lift,
        y: s.ay + dy * e - dx * lift,
        down: false,
        revealed: this.revealedBefore(lo),
      };
    }
    // drawing: constant speed with a gentle settle at both ends
    const k = s.stroke;
    const v = 0.7 * u + 0.3 * u * u * (3 - 2 * u);
    const n = set.count[k];
    const i0 = set.first[k];
    if (n === 1) return { x: set.x[i0], y: set.y[i0], down: true, revealed: i0 + 1 };
    const f = v * (n - 1);
    const j = Math.min(n - 2, Math.floor(f));
    const w = f - j;
    return {
      x: lerp(set.x[i0 + j], set.x[i0 + j + 1], w),
      y: lerp(set.y[i0 + j], set.y[i0 + j + 1], w),
      down: true,
      revealed: i0 + Math.min(n, Math.floor(f + 1e-9) + 1),
    };
  }

  /** points revealed when segment `idx` starts */
  private revealedBefore(idx: number): number {
    for (let j = idx; j < this.segs.length; j++) {
      if (this.segs[j].stroke >= 0) return this.set.first[this.segs[j].stroke];
    }
    return this.set.total;
  }
}

/**
 * Fit pen-up travel times into a budget. When the desired times don't fit, long moves keep at least
 * `longMin` seconds (so they stay visible) and the opening travel optionally keeps its full time;
 * everything else shrinks proportionally.
 */
export function fitTravel(
  desired: number[],
  dist: number[],
  budget: number,
  o: { exemptFirst?: boolean; longDist?: number; longMin?: number } = {},
): number[] {
  const longDist = o.longDist ?? 120;
  const longMin = o.longMin ?? 0.1;
  const total = desired.reduce((a, b) => a + b, 0);
  if (total <= budget || total === 0) return [...desired];
  const out = [...desired];
  // the opening travel keeps its full time (up to the whole budget); the hops share what's left
  const first = o.exemptFirst ? desired.findIndex((t) => t > 0) : -1;
  if (first >= 0) out[first] = Math.min(desired[first], budget);
  const left = Math.max(0, budget - (first >= 0 ? out[first] : 0));
  const hops = desired.map((_, i) => i !== first && desired[i] > 0);
  const reserved = desired.map((t, i) => (hops[i] && dist[i] >= longDist ? Math.min(t, longMin) : 0));
  const rsum = reserved.reduce((a, b) => a + b, 0);
  if (rsum >= left) {
    const g = rsum > 0 ? left / rsum : 0;
    return out.map((t, i) => (hops[i] ? reserved[i] * g : t));
  }
  const extra = desired.map((t, i) => (hops[i] ? t - reserved[i] : 0));
  const esum = extra.reduce((a, b) => a + b, 0);
  const f = esum > 0 ? (left - rsum) / esum : 0;
  return out.map((t, i) => (hops[i] ? reserved[i] + extra[i] * f : t));
}

/**
 * A back-and-forth scrubbing path covering a w × h box (for erasers): rows `2r·0.8` apart, with
 * overshoot so the brush reaches the edges.
 */
export function zigzagStroke(x0: number, y0: number, w: number, h: number, r: number): PlanStroke {
  const rows = Math.max(1, Math.ceil(h / (1.6 * r)));
  const dy = rows > 1 ? (h - 2 * r * 0.5) / (rows - 1) : 0;
  const p: number[] = [];
  for (let i = 0; i < rows; i++) {
    const y = y0 + (rows > 1 ? r * 0.5 + i * dy : h / 2);
    const [a, b] = i % 2 === 0 ? [x0 - r * 0.3, x0 + w + r * 0.3] : [x0 + w + r * 0.3, x0 - r * 0.3];
    p.push(a, y, b, y);
  }
  return { p, r };
}
